import { ArrowDownLeft, ArrowUpRight, ExternalLink, Landmark } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Badge } from "@/components/ui";
import { resolveWalletMovementPresentation } from "@/lib/walletMovementLabels";
import { cn } from "@/lib/utils";
import { formatMoney } from "@/lib/money";
import { formatDate, formatDateTime } from "@/lib/datetime";
import type { WalletTransaction } from "@/types";

interface TransactionItemProps {
  transaction: WalletTransaction;
  /** Show compact version without extra details */
  compact?: boolean;
  /** Resolve booking detail URL; defaults to `/bookings/:id` */
  getBookingPath?: (bookingId: string) => string;
}

function defaultBookingPath(bookingId: string): string {
  return `/bookings/${bookingId}`;
}

/**
 * Reusable transaction item component for displaying wallet transactions.
 * Used in Dashboard and Wallet pages.
 */
export function TransactionItem({
  transaction,
  compact = false,
  getBookingPath = defaultBookingPath,
}: TransactionItemProps) {
  const { t } = useTranslation("components");
  const presentation = useMemo(
    () => resolveWalletMovementPresentation(transaction, t),
    [transaction, t],
  );

  const isCredit = transaction.type === "CREDIT";
  const isSettlement = presentation.movementKind === "settlement_transfer";
  const isRefund = presentation.movementKind === "refund";
  const isIncome = isCredit && !isSettlement;
  const bookingId = transaction.booking_id;
  const bookingPath = bookingId ? getBookingPath(bookingId) : null;

  const iconStyles = isSettlement
    ? "bg-primary-50 text-primary-600 ring-1 ring-inset ring-primary-600/15"
    : isRefund
      ? "bg-warning-50 text-warning-600 ring-1 ring-inset ring-warning-600/15"
      : isIncome
        ? "bg-success-50 text-success-600 ring-1 ring-inset ring-success-600/15"
        : "bg-error-50 text-error-600 ring-1 ring-inset ring-error-600/15";

  const amountStyles = isSettlement
    ? "text-primary-700"
    : isRefund
      ? "text-warning-700"
      : isIncome
        ? "text-success-600"
        : "text-error-600";

  const badgeVariant = isSettlement
    ? "default"
    : isRefund
      ? "warning"
      : isIncome
        ? "success"
        : "error";

  const content = (
    <>
      <div
        className={cn(
          "flex shrink-0 items-center justify-center rounded-lg",
          compact ? "h-8 w-8" : "h-9 w-9",
          iconStyles,
        )}
      >
        {isSettlement ? (
          <Landmark className="h-4 w-4" />
        ) : isCredit ? (
          <ArrowDownLeft className="h-4 w-4" />
        ) : (
          <ArrowUpRight className="h-4 w-4" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-surface-900">{presentation.title}</p>

            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[13px] text-surface-500">
              <span>{formatDateTime(transaction.created_at)}</span>

              {transaction.activity_name ? (
                <>
                  <span className="text-surface-300">•</span>
                  <span className="font-medium text-surface-700">
                    {transaction.activity_name}
                  </span>
                </>
              ) : null}

              {transaction.visit_at ? (
                <>
                  <span className="text-surface-300">•</span>
                  <span>
                    {t("transactionVisitDate", {
                      date: formatDate(transaction.visit_at),
                    })}
                  </span>
                </>
              ) : null}

              {!compact &&
                "service_name" in transaction &&
                transaction.service_name && (
                  <>
                    <span className="text-surface-300">•</span>
                    <Badge variant="outline" size="sm">
                      {transaction.service_name}
                    </Badge>
                  </>
                )}

              {bookingPath ? (
                <>
                  <span className="text-surface-300">•</span>
                  <span className="inline-flex items-center gap-1 font-medium text-primary-600">
                    <ExternalLink className="h-3.5 w-3.5" />
                    {t("transactionOpenBooking")}
                  </span>
                </>
              ) : null}
            </div>

            {!compact &&
              "customer_name" in transaction &&
              transaction.customer_name && (
                <p className="mt-1 text-sm text-surface-500">
                  {t("transactionCustomer")}{" "}
                  <span className="font-medium">
                    {transaction.customer_name}
                  </span>
                </p>
              )}
          </div>

          <div className="text-right">
            <p
              className={cn(
                "font-semibold tabular-nums",
                compact ? "text-sm" : "text-base",
                amountStyles,
              )}
            >
              {isCredit ? "+" : "-"}
              {formatMoney(transaction.amount)}
            </p>
            <Badge variant={badgeVariant} size="sm">
              {presentation.badgeLabel}
            </Badge>
          </div>
        </div>
      </div>
    </>
  );

  const className = cn(
    "flex items-start gap-3 transition-colors",
    compact
      ? "rounded-lg px-3 py-2.5 hover:bg-surface-50 dark:hover:bg-surface-100/50"
      : "rounded-xl border border-surface-200 bg-surface-0 p-3.5 hover:border-surface-300 hover:bg-surface-50",
    bookingPath && "cursor-pointer",
  );

  if (bookingPath) {
    return (
      <Link to={bookingPath} className={className}>
        {content}
      </Link>
    );
  }

  return <div className={className}>{content}</div>;
}
